import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportScope } from '@/api/support/supportApi.types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import {
  useMarkSupportTicketReadMutation,
  useSendSupportMessageMutation,
  useSupportMessagesQuery,
  type SupportMessageWithSendStatus,
} from '@/hooks/useSupport';
import { SupportComposer } from './SupportComposer';
import { SupportMessageBubble } from './SupportMessageBubble';

interface TicketThreadProps {
  scope: SupportScope;
  ticketId: string;
  ownSenderKind: string;
  isSendEnabled: boolean;
  disabledNotice: string;
}

export function TicketThread({
  scope,
  ticketId,
  ownSenderKind,
  isSendEnabled,
  disabledNotice,
}: TicketThreadProps) {
  const { t } = useTranslation('support');
  const messagesQuery = useSupportMessagesQuery(scope, ticketId);
  const markReadMutation = useMarkSupportTicketReadMutation(scope, ticketId);
  const retryMutation = useSendSupportMessageMutation(scope, ticketId, ownSenderKind);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messages = messagesQuery.data === undefined ? [] : messagesQuery.data;
  const messageCount = messages.length;
  const newestMessage = messages[messageCount - 1];
  const newestIsFromCounterpart =
    newestMessage !== undefined && newestMessage.senderKind !== ownSenderKind;

  useEffect(
    function scrollToNewestMessage() {
      if (messagesEndRef.current !== null) {
        messagesEndRef.current.scrollIntoView({ block: 'end' });
      }
    },
    [messageCount]
  );

  const markRead = markReadMutation.mutate;
  useEffect(
    function markThreadRead() {
      if (newestIsFromCounterpart === true) {
        markRead();
      }
    },
    [newestIsFromCounterpart, messageCount, markRead]
  );

  function retryMessage(message: SupportMessageWithSendStatus) {
    retryMutation.mutate({
      tempId: message.id,
      body: message.body,
      attachmentIds: [],
    });
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-zoooffwhite">
        {messagesQuery.isLoading === true ? (
          <div className="py-10 flex justify-center">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        ) : messagesQuery.isError === true ? (
          <QueryErrorState
            onRetry={function retry() {
              messagesQuery.refetch();
            }}
          />
        ) : messageCount === 0 ? (
          <p className="py-10 text-center text-[13px] text-zoomuted italic">
            {t('support:thread.empty')}
          </p>
        ) : (
          messages.map(function renderMessage(message) {
            return (
              <SupportMessageBubble
                key={message.id}
                scope={scope}
                message={message}
                isOwnMessage={message.senderKind === ownSenderKind}
                onRetry={retryMessage}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      <SupportComposer
        scope={scope}
        ticketId={ticketId}
        ownSenderKind={ownSenderKind}
        isSendEnabled={isSendEnabled}
        disabledNotice={disabledNotice}
      />
    </div>
  );
}
